import type { IconName } from "@nexus/ui";
import type { AdminOpsSettingKey } from "@/features/admin/lib/admin-ops-settings";

// ─── Ops settings groups ────────────────────────────────────────────
// The operations settings are one saved record, but they answer five unrelated
// questions and reading all of them in one scroll meant nobody found anything.
// Each group is one card and one rail entry; the record and its save footer
// stay shared.

export const OPS_SETTINGS_GROUPS = [
  "events",
  "jobs",
  "tokenUsage",
  "tokenAlerts",
  "cost",
  "bigQuery",
] as const;

export type OpsSettingsGroup = (typeof OPS_SETTINGS_GROUPS)[number];

/** Where a deep link with no group lands: the tab the panel itself opens on. */
export const DEFAULT_OPS_SETTINGS_GROUP: OpsSettingsGroup = "events";

interface OpsGroupMeta {
  icon: IconName;
  labelKey: string;
  labelFallback: string;
  /**
   * The settings this group owns. Every editable key belongs to exactly one
   * group — a key in none of them would have no control anywhere, and a key in
   * two would show an unsaved marker on both rails at once.
   */
  keys: readonly AdminOpsSettingKey[];
}

export const OPS_SETTINGS_GROUP_META: Record<OpsSettingsGroup, OpsGroupMeta> = {
  events: {
    icon: "history",
    labelKey: "admin.opsSettings.groups.events",
    labelFallback: "Events log",
    keys: [
      "eventsRetentionDays",
      "eventsHidePingsDefault",
      "eventsDefaultWindowHours",
      "eventsStuckAfterMinutes",
      "alertOnFailedEvents",
      "failedEventsAlertThreshold",
      "alertOnSlowActions",
      "slowActionSecondsDefault",
      "slowActionSecondsByEventType",
      "eventsChartLatencyMetric",
      "eventsChartShowBudgetLine",
    ],
  },
  jobs: {
    icon: "clock",
    labelKey: "admin.opsSettings.groups.jobs",
    labelFallback: "Scheduled jobs",
    keys: [
      "jobsOverdueGraceMinutes",
      "jobsUnhealthyAfterFailures",
      "jobsAutoRefreshSeconds",
      "alertOnJobFailures",
      "alertOnJobMissed",
      "alertOnJobLifecycle",
    ],
  },
  tokenUsage: {
    icon: "sparkles",
    labelKey: "admin.opsSettings.groups.tokenUsage",
    labelFallback: "Token usage",
    keys: [
      "tokenUsageRetentionDays",
      "tokenUsageDefaultWindowHours",
      "tokenUsageAutoRefreshSeconds",
      "tokenUsageDefaultGrouping",
      "tokenUsageLongTailPercent",
    ],
  },
  tokenAlerts: {
    icon: "flag",
    labelKey: "admin.opsSettings.groups.tokenAlerts",
    labelFallback: "Token alerts",
    keys: [
      "tokenSpendAlertWindowHours",
      "tokenSpendAlertCooldownHours",
      "alertOnTokenSpend",
      "tokenSpendAlertThreshold",
      "alertOnTenantTokenSpend",
      "tenantTokenSpendAlertThreshold",
      "alertOnLlmLatency",
      "llmLatencyAlertP95Ms",
    ],
  },
  cost: {
    icon: "target",
    labelKey: "admin.opsSettings.groups.cost",
    labelFallback: "Cost & prices",
    keys: ["monthlyBudgetUsdCents", "budgetAlertAtPercent"],
  },
  bigQuery: {
    // No warehouse or database glyph in the registry; the columns one is the
    // closest thing to a table it carries.
    icon: "columns",
    labelKey: "admin.opsSettings.groups.bigQuery",
    labelFallback: "BigQuery usage",
    keys: [
      "bigQueryPricePerTbMicros",
      "bigQueryRetentionDays",
      "alertOnBigQueryBytes",
      "bigQueryBytesAlertThreshold",
      "alertOnBigQuerySingleQueryBytes",
      "bigQuerySingleQueryBytesThreshold",
      "bigQueryBytesAlertWindowHours",
      "bigQueryBytesAlertCooldownHours",
    ],
  },
};

/**
 * Which groups hold an unsaved change. Splitting the page introduced the risk
 * of an edit sitting on a card nobody is looking at while the footer reports a
 * count without saying where — this is what the rail marks it with.
 */
export function groupsWithChanges(changedKeys: readonly string[]): Set<OpsSettingsGroup> {
  const changed = new Set(changedKeys);

  return new Set(
    OPS_SETTINGS_GROUPS.filter((group) =>
      OPS_SETTINGS_GROUP_META[group].keys.some((key) => changed.has(key)),
    ),
  );
}
